<template>
  <div class="soft-skills-growth-card">
    <div class="soft-skills-growth-card__score">
      {{ formattedScore }}
    </div>

    <div class="soft-skills-growth-card__change">
      <span
          :class="{
            'positive-change': safeChange > 0,
            'negative-change': safeChange < 0,
            'neutral-change': safeChange === 0
          }"
      >
        {{ changeText }}
      </span>

      <span>{{ changeLabel }}</span>
    </div>

    <div class="soft-skills-growth-card__indexes">
      <div
          v-for="item in normalizedIndexes"
          :key="item.label"
          class="soft-skills-growth-card__index-row"
      >
        <span>{{ item.label }}</span>

        <span class="soft-skills-growth-card__index-value">
          {{ item.value }} чел.
        </span>
      </div>
    </div>

    <div class="soft-skills-growth-card__chart">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from 'chart.js'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Filler,
    Tooltip,
)

export default {
  name: 'SoftSkillsGrowthCard',

  props: {
    score: {
      type: Number,
      default: 0,
    },
    change: {
      type: Number,
      default: 0,
    },
    changeLabel: {
      type: String,
      default: '',
    },
    indexes: {
      type: Array,
      default: () => [],
    },
    chartLabels: {
      type: Array,
      default: () => [],
    },
    chartValues: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const chartCanvas = ref(null)
    let chartInstance = null

    const safeScore = computed(() => {
      const value = Number(props.score)

      return Number.isFinite(value) ? value : 0
    })

    const safeChange = computed(() => {
      const value = Number(props.change)

      return Number.isFinite(value) ? value : 0
    })

    const formattedScore = computed(() => {
      return Number.isInteger(safeScore.value)
          ? safeScore.value
          : safeScore.value.toFixed(1)
    })

    const changeText = computed(() => {
      if (safeChange.value > 0) {
        return `+${safeChange.value}%`
      }

      if (safeChange.value < 0) {
        return `${safeChange.value}%`
      }

      return '0%'
    })

    const normalizedIndexes = computed(() => {
      return props.indexes.map((item) => {
        const value = Number(item.value)

        return {
          ...item,
          value: Number.isFinite(value) ? value : 0,
        }
      })
    })

    const getChartGradient = (ctx, chartArea) => {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)

      gradient.addColorStop(0, 'rgba(33, 172, 12, 0.28)')
      gradient.addColorStop(1, 'rgba(33, 172, 12, 0)')

      return gradient
    }

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy()
        chartInstance = null
      }
    }

    const renderChart = async () => {
      await nextTick()

      if (!chartCanvas.value) {
        return
      }

      destroyChart()

      const ctx = chartCanvas.value.getContext('2d')

      chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: props.chartLabels,
          datasets: [
            {
              data: props.chartValues,
              borderColor: '#21AC0C',
              backgroundColor: (context) => {
                const chart = context.chart
                const { chartArea } = chart

                if (!chartArea) {
                  return 'rgba(33, 172, 12, 0.14)'
                }

                return getChartGradient(chart.ctx, chartArea)
              },
              borderWidth: 1,
              pointRadius: 3,
              pointHoverRadius: 4,
              pointBackgroundColor: '#21AC0C',
              pointBorderColor: '#21AC0C',
              fill: true,
              tension: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,

          layout: {
            padding: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            },
          },

          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              displayColors: true,
            },
            datalabels: {
              display: false,
            },
          },

          scales: {
            x: {
              offset: false,
              grid: {
                color: '#EEF0F3',
                drawTicks: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: 'rgba(0, 0, 0, 0.45)',
                padding: 8,
                font: {
                  size: 12,
                },
              },
            },

            y: {
              min: -50,
              max: 50,
              bounds: 'ticks',
              ticks: {
                stepSize: 25,
                padding: 6,
                color: 'rgba(0, 0, 0, 0.45)',
                font: {
                  size: 12,
                },
                callback: (value) => {
                  if (value > 0) {
                    return `+${value}`
                  }

                  return value
                },
              },
              grid: {
                drawTicks: false,
                drawBorder: false,
                color: (context) => {
                  if (context.tick.value === 50) {
                    return 'transparent'
                  }

                  return '#EEF0F3'
                },
              },
              border: {
                display: false,
              },
            },
          },
        }
      })
    }

    onMounted(() => {
      renderChart()
    })

    onBeforeUnmount(() => {
      destroyChart()
    })

    watch(
        () => [props.chartLabels, props.chartValues],
        () => {
          renderChart()
        },
        {
          deep: true,
        }
    )

    return {
      chartCanvas,
      formattedScore,
      safeChange,
      changeText,
      normalizedIndexes,
    }
  },
}
</script>

<style scoped lang="scss">
.soft-skills-growth-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.soft-skills-growth-card__score {
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 30px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.soft-skills-growth-card__change {
  display: flex;
  gap: 4px;
  margin-bottom: 22px;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.soft-skills-growth-card__change span:first-child {
  font-weight: 600;
}

.soft-skills-growth-card__indexes {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 24px;
}

.soft-skills-growth-card__index-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.soft-skills-growth-card__index-value {
  color: rgba(0, 0, 0, 0.88);
  text-decoration: underline;
  white-space: nowrap;
}

.soft-skills-growth-card__chart {
  width: 100%;
  height: 122px;
  margin-top: auto;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}
</style>